import { useEffect } from 'react';

import type { CompositionPageScope } from '@revelio/core/global-ai-assistant/composition-page-scope';

import {
  changesPageScope,
  useGlobalAiCommandStore,
} from './global-ai-assistant-command-store';

/**
 * Publishes the mounted page's filters into the command store for the engine
 * to send with each turn, and clears them when the page goes away.
 *
 * Its own file, called by the bridge, because the ORDER of the two effects is
 * the whole contract and the bridge is untestable without most of CustomDash:
 * folded into one effect, the cleanup runs before every re-run, so an
 * unrelated filter edit would delete the entry and re-add it — the store's
 * skip-if-unchanged could never fire (it compares against an entry that was
 * just deleted) and every edit would re-render the engine, which subscribes
 * with a whole-object selector.
 */
export function usePublishCurrentPageFilters(args: {
  enabled: boolean;
  pathname: string;
  filters: CompositionPageScope;
  /**
   * The reducer's own `taxonomyReady`, which `deriveAdaptiveTaxonomies` writes
   * atomically with the role, skill AND activity values — the same state this
   * publishes, rather than the readiness of the preferences behind it.
   * Publishing early is a wrong-answer path, not a cosmetic race: role and
   * skill ids are taxonomy-relative, so an artifact built under an unsettled
   * taxonomy carries real ids that name different roles under the tree the
   * server will read them against.
   */
  isTaxonomyResolved: boolean;
  isDateResolved?: boolean;
}): void {
  const {
    enabled,
    pathname,
    filters,
    isTaxonomyResolved,
    isDateResolved = true,
  } = args;
  const queue = useGlobalAiCommandStore((state) => state.queue);
  const publishCurrentPageFilters = useGlobalAiCommandStore(
    (state) => state.publishCurrentPageFilters
  );

  useEffect(() => {
    const filtersPending = queue.some(({ command }) =>
      changesPageScope(command)
    );
    // Clear stale scope when disabled, applying filters, or resolving taxonomies.
    // Returning early would leave the previous population readable.
    if (!enabled || !isTaxonomyResolved || !isDateResolved || filtersPending) {
      publishCurrentPageFilters(pathname, undefined);
      return;
    }
    publishCurrentPageFilters(pathname, filters);
  }, [
    enabled,
    isTaxonomyResolved,
    isDateResolved,
    queue,
    pathname,
    filters,
    publishCurrentPageFilters,
  ]);

  // Keyed on the path alone: clearing must happen when the page changes or
  // unmounts, and NEVER between two publishes for the same page.
  useEffect(() => {
    return () => {
      // A page's filters must not outlive the page: the next turn would
      // otherwise scope an answer to somewhere the user has left.
      publishCurrentPageFilters(pathname, undefined);
    };
  }, [pathname, publishCurrentPageFilters]);
}
